import { Select } from "antd";
import _ from "lodash";
import React from "react";
import { useForm, Controller } from "react-hook-form";
import { toast } from "react-toastify";
import styled from "styled-components";

import { Button } from "@components/Button";
import ErrorValidate from "@components/ErrorValidate";
import { StyledInput } from "@components/Input";
import { Text } from "@components/Text";
import { COLOR_DARK, COLOR_GREEN, COLOR_WHITE } from "@static/COLORS";
import { fetchAPI } from "@utils/https/fetchAPI";
import { VALIDATE_RULES } from "@validate/index";

const FormContainer = styled.form`
  width: 100%;
  height: 100%;
  display: flex;
  position: relative;
  flex-direction: column;
`;

const Line = styled.div`
  width: 100%;
  height: 1px;
  margin-bottom: 8px;
  background-color: rgba(0, 0, 0, 0.1);
`;

const InnerContainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  flex-grow: 1;
`;

const ButtonContainer = styled.div`
  display: flex;
  justify-content: center;
  height: 40px;
`;

export const FormEdit = ({
  setDataList = () => undefined,
  setIsModalVisible,
  selectedData,
  setSelectedData,
  customerOptions = [],
  userProfile,
}) => {
  const id = _.get(selectedData, ["id"]);
  const name = _.get(selectedData, ["name"]);
  const category_customer_id = _.get(selectedData, ["category_customer_id"]);
  const disk_limit = _.get(selectedData, ["disk_limit"]);
  const user_type = _.get(userProfile, ["user_type"]);

  const { control, handleSubmit } = useForm({
    defaultValues: {
      name: name,
      customerType: category_customer_id,
      disk_limit: disk_limit,
    },
  });

  const onHandleEdit = async (data) => {
    const name = _.get(data, ["name"]);
    const disk_limit = _.get(data, ["disk_limit"]);
    const category_customer_id = _.get(data, ["customerType"]);

    const resp = await fetchAPI({
      method: "post",
      path: "/api/v1/location-update",
      isAuth: true,
      data: {
        id,
        name,
        disk_limit,
        category_customer_id,
        user_type,
      },
    });
    if (_.get(resp, ["status"])) {
      const results = _.chain(resp).get(["result"]).value();
      const updateData = _.chain(results)
        .thru((item) => {
          return {
            id: _.get(item, ["id"]),
            name: _.get(item, ["name"]),
            disk_limit: _.get(item, ["disk_limit"]),
            ...item,
          };
        })
        .value();
      toast.success("Update Location Success.");
      setIsModalVisible((prev) => ({ ...prev, isOpen: false }));
      setSelectedData(null);
      setDataList((prev) => {
        const updateDataList = _.map(prev, (item) => {
          return _.get(item, ["id"]) ===
            _.chain(results).get(["id"]).toInteger().value()
            ? { ...item, ...updateData }
            : item;
        });
        return updateDataList;
      });
    }
  };

  return (
    <FormContainer onSubmit={handleSubmit(onHandleEdit)} autoComplete="off">
      <Line />
      <InnerContainer>
        <div>
          <Controller
            name="name"
            control={control}
            rules={_.get(VALIDATE_RULES, ["required"])}
            render={({ field, fieldState: { error } }) => (
              <div>
                <Text
                  color={COLOR_DARK}
                  fontSize="14px"
                  style={{ marginBottom: 4, marginLeft: 4 }}
                >
                  Name
                </Text>
                <StyledInput
                  label="name"
                  {...field}
                  size="large"
                  placeholder="name"
                />
                <ErrorValidate justify="start" error={error} />
              </div>
            )}
          />

          <Controller
            name="customerType"
            control={control}
            rules={_.get(VALIDATE_RULES, ["required"])}
            render={({ field, fieldState: { error } }) => (
              <div>
                <Text
                  color={COLOR_DARK}
                  fontSize="14px"
                  style={{ marginBottom: 4, marginLeft: 4 }}
                >
                  Customer
                </Text>
                <Select
                  {...field}
                  size="large"
                  showSearch
                  placeholder="Select a customer"
                  optionFilterProp="children"
                  style={{ width: "100%" }}
                  filterOption={(input, option) =>
                    (option?.label ?? "").includes(input)
                  }
                  filterSort={(optionA, optionB) =>
                    (optionA?.label ?? "")
                      .toLowerCase()
                      .localeCompare((optionB?.label ?? "").toLowerCase())
                  }
                  options={customerOptions}
                />
                <ErrorValidate justify="start" error={error} />
              </div>
            )}
          />

          <Controller
            name="disk_limit"
            control={control}
            rules={_.get(VALIDATE_RULES, ["number"])}
            render={({ field, fieldState: { error } }) => (
              <div>
                <Text
                  color={COLOR_DARK}
                  fontSize="14px"
                  style={{ marginBottom: 4, marginLeft: 4 }}
                >
                  Disk limit (GB)
                </Text>
                <StyledInput
                  label="disk_limit"
                  {...field}
                  size="large"
                  placeholder="Disk limit"
                />
                <ErrorValidate justify="start" error={error} />
              </div>
            )}
          />
        </div>

        <ButtonContainer>
          <Button
            type="submit"
            color={COLOR_GREEN}
            fontColor={COLOR_WHITE}
            fontSize="16px"
            padding="5px 20px"
          >
            Save
          </Button>
        </ButtonContainer>
      </InnerContainer>
    </FormContainer>
  );
};
